import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faClipboardList, faUser } from "@fortawesome/free-solid-svg-icons";

import { EMPTY, codeOf } from "../../crmpostventa-shared/crmFormat";
import { gestionHref, isPlainClick } from "../../crmpostventa-shared/crmLinks";
import { ESTADO_LABELS, TIPO_LABELS, motivoLabel } from "../../crmpostventa-ipc/ipcFormat";
import { formatFechaHora, formatHace, formatHistorialValor, isLegacy, origenLabel } from "../fichaFormat";

const Valor = ({ value }) => {
  const text = formatHistorialValor(value);
  return text === EMPTY ? <span className="crm-rollo__muted">{EMPTY}</span> : text;
};

const AutoTag = ({ campo }) => (campo.automatico ? <span className="crm-ficha__auto">Se completó solo</span> : null);

const keyOf = (campo, index) => `${campo.campo || campo.etiqueta}-${index}`;

function OrigenChip({ item }) {
  return <span className={`crm-rollo__chip crm-ficha__origin${isLegacy(item) ? " is-legacy" : ""}`}>{origenLabel(item.origen)}</span>;
}

function Motivo({ item }) {
  return (
    <>
      {item.motivo ? (
        <div className="crm-ficha__motivo">
          <span>Motivo</span>
          {item.motivo}
        </div>
      ) : null}
      {item.observacion ? (
        <div className="crm-ficha__motivo">
          <span>Observación</span>
          {item.observacion}
        </div>
      ) : null}
    </>
  );
}

// "Modificación · Datos bancarios · Resuelta" of the gestión summary; "N° 112" when the gestión is not known.
const gestionNombre = (item) => {
  const gestion = item.gestion;
  if (!gestion) return `N° ${item.gestionId}`;
  const tipo = TIPO_LABELS[codeOf(gestion.tipo)] || gestion.tipo || TIPO_LABELS.MODIFICACION;
  const motivo = gestion.motivoEtiqueta || motivoLabel(gestion);
  const estado = ESTADO_LABELS[codeOf(gestion.estado)] || gestion.estado;
  return [tipo, motivo, estado].filter(Boolean).join(" · ");
};

// Gestión linked to the change, with "Ver gestión" (Gestiones IPC opens its Gestionar).
function GestionLine({ item, clienteId, onNavigate }) {
  const gestionId = item.gestion?.id || item.gestionId;
  if (!gestionId) return null;
  const href = gestionHref({ clienteId, gestionId });
  return (
    <div className="crm-ficha__gline">
      <FontAwesomeIcon icon={faClipboardList} aria-hidden="true" />
      <span>
        Gestión <b>{gestionNombre({ ...item, gestionId })}</b>{" "}
        <a
          className="crm-rollo__link"
          href={href}
          onClick={(event) => {
            if (!onNavigate || !isPlainClick(event)) return;
            event.preventDefault();
            onNavigate(href);
          }}
        >
          Ver gestión
        </a>
      </span>
    </div>
  );
}

// A legacy row that could not be read as "Campo: antes -> ahora" lines keeps its raw text.
function LegacyText({ item }) {
  const campos = item.campos || [];
  if (campos.length || !item.textoOriginal) return null;
  return (
    <div className="crm-ficha__legacy">
      <span>Registro del sistema anterior</span>
      <p>{item.textoOriginal}</p>
    </div>
  );
}

function CampoLabel({ campo, tituloDe }) {
  const titulo = campo.seccion ? tituloDe(campo.seccion) : "";
  return (
    <>
      {titulo ? <span className="crm-rollo__muted">{`${titulo} · `}</span> : null}
      {campo.etiqueta || campo.campo || EMPTY}
      <AutoTag campo={campo} />
    </>
  );
}

function EntryTable({ items, tituloDe, clienteId, today, nuevoId, onNavigate }) {
  return (
    <div className="crm-rollo__table-wrap">
      <table className="crm-rollo__table crm-ficha__table">
        <thead>
          <tr>
            <th className="crm-ficha__col-campo">Campo</th>
            <th>Antes</th>
            <th>Ahora</th>
          </tr>
        </thead>
        {items.map((item) => {
          const hace = formatHace(item.fecha, today);
          return (
            <tbody key={item.id} className={`crm-ficha__entry${item.id === nuevoId ? " is-new" : ""}`}>
              <tr className="crm-ficha__evrow">
                <td colSpan={3}>
                  <div className="crm-ficha__evline">
                    <span className="crm-ficha__when">
                      <b>{formatFechaHora(item.fecha)}</b>
                      {item.id === nuevoId ? <span className="crm-rollo__new-tag">Nueva</span> : null}
                      {hace ? <span className="crm-rollo__muted">{` · ${hace}`}</span> : null}
                    </span>
                    <span className="crm-ficha__actor">
                      <FontAwesomeIcon icon={faUser} aria-hidden="true" />
                      <span>{item.usuario?.nombre || EMPTY}</span>
                    </span>
                    <OrigenChip item={item} />
                  </div>
                  <Motivo item={item} />
                  <GestionLine item={item} clienteId={clienteId} onNavigate={onNavigate} />
                  <LegacyText item={item} />
                </td>
              </tr>
              {(item.campos || []).map((campo, index) => (
                <tr key={keyOf(campo, index)} className="crm-ficha__change">
                  <td className="crm-ficha__campo">
                    <CampoLabel campo={campo} tituloDe={tituloDe} />
                  </td>
                  <td className="crm-ficha__old">
                    <Valor value={campo.anterior} />
                  </td>
                  <td className="crm-ficha__new">
                    <Valor value={campo.nuevo} />
                  </td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

function EntryCards({ items, tituloDe, clienteId, today, nuevoId, onNavigate }) {
  return (
    <div className="crm-ficha__mcards">
      {items.map((item) => (
        <article key={item.id} className={`crm-ficha__entry crm-ficha__mcard${item.id === nuevoId ? " is-new" : ""}`}>
          <div className="crm-ficha__evmeta">
            <b>{formatFechaHora(item.fecha)}</b>
            <span className="crm-rollo__muted">{[formatHace(item.fecha, today), item.usuario?.nombre || EMPTY].filter(Boolean).join(" · ")}</span>
          </div>
          <div>
            <OrigenChip item={item} />
          </div>
          <Motivo item={item} />
          <GestionLine item={item} clienteId={clienteId} onNavigate={onNavigate} />
          <LegacyText item={item} />
          {(item.campos || []).length ? (
            <ul className="crm-ficha__changes">
              {item.campos.map((campo, index) => (
                <li key={keyOf(campo, index)} className="crm-ficha__change">
                  <span className="crm-ficha__campo">
                    <CampoLabel campo={campo} tituloDe={tituloDe} />
                  </span>
                  <span className="crm-ficha__old">
                    <span className="crm-rollo__sr-only">Antes: </span>
                    <Valor value={campo.anterior} />
                  </span>
                  <span className="crm-ficha__arrow" aria-hidden="true">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                  <span className="crm-ficha__new">
                    <span className="crm-rollo__sr-only">Ahora: </span>
                    <Valor value={campo.nuevo} />
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </article>
      ))}
    </div>
  );
}

/**
 * Saved changes grouped by change, one row per field (Campo · Antes · Ahora): a table on desktop and cards on mobile.
 * Each change shows date, asesor, origin (Ficha, Gestión IPC, CRM anterior), motive, the linked gestión and, for a
 * legacy row that could not be read, its raw text. tituloDe(seccion) names the block of a field.
 */
export default function HistorialCambios({ items, isMobile, ...props }) {
  return isMobile ? <EntryCards items={items} {...props} /> : <EntryTable items={items} {...props} />;
}
